import './sentry';
import { FluentProvider } from '@fluentui/react-components';
import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { RouterProvider } from '@tanstack/react-router';
import React, { lazy, useEffect } from 'react';
import ReactDOM from 'react-dom/client';

import './global.css';
import { ErrorFallback } from './components/ErrorFallback';
import { initI18n } from './lib/i18n';
import { localStoragePersister } from './lib/query-persister';
import { themes } from './lib/theme';
import { ThemeProvider, useTheme } from './lib/theme/ThemeContext';
import { startVersionCheck } from './lib/version-check';
import { router } from './router';

// Lazy load devtools only in development
const ReactQueryDevtools =
  import.meta.env.VITE_ENV === 'development'
    ? lazy(() =>
        import('@tanstack/react-query-devtools').then((res) => ({
          default: res.ReactQueryDevtools,
        })),
      )
    : () => null;

function ThemedApp({ queryClient }: { queryClient: QueryClient }) {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    document.body.style.backgroundColor = themes[resolvedTheme].colorNeutralBackground1;
  }, [resolvedTheme]);

  return (
    <FluentProvider theme={themes[resolvedTheme]}>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: localStoragePersister }}>
        <Sentry.ErrorBoundary fallback={(props) => <ErrorFallback resetError={() => props.resetError()} />}>
          <RouterProvider router={router} context={{ queryClient }} />
        </Sentry.ErrorBoundary>
        <ReactQueryDevtools initialIsOpen={false} />
      </PersistQueryClientProvider>
    </FluentProvider>
  );
}

function renderApp() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000, // 1 minute
        gcTime: 1000 * 60 * 60 * 24, // 24 hours - required for persistence
      },
    },
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ThemeProvider>
        <ThemedApp queryClient={queryClient} />
      </ThemeProvider>
    </React.StrictMode>,
  );
}

startVersionCheck();

// Initialize i18n, then render (render anyway if i18n fails)
initI18n()
  .catch((error) => {
    console.error('Failed to initialize i18n:', error);
  })
  .finally(() => {
    renderApp();
  });
